import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeaderNav from "@/components/HeaderNav";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wrench, Smartphone, Laptop, Tablet, HelpCircle, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const DEVICE_TYPES = [
  { value: "mobile", label: "Mobile Phone", Icon: Smartphone },
  { value: "laptop", label: "Laptop", Icon: Laptop },
  { value: "tablet", label: "Tablet / iPad", Icon: Tablet },
  { value: "other", label: "Other", Icon: HelpCircle },
];

const COMMON_ISSUES = [
  "Broken/cracked screen", "Battery not charging", "Battery drains fast",
  "Speaker / mic issue", "Camera not working", "Water damage",
  "Not turning on", "Charging port issue", "Software / stuck at logo",
  "Keyboard / touchpad issue (laptop)", "Overheating", "Other",
];

export default function BookRepair() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(null);
  const [config, setConfig] = useState({ business_whatsapp: "918104878405" });
  const [form, setForm] = useState({
    device_type: "mobile",
    brand: "",
    model: "",
    issue: "",
    customer_name: "",
    customer_phone: "",
    service_mode: "walk-in",
    preferred_date: "",
    address: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand || !form.model || !form.issue || !form.customer_name || !form.customer_phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (form.service_mode === "doorstep-pickup" && !form.address) {
      toast.error("Address is required for doorstep pickup");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/repair", form);
      setSubmitted(data);
      toast.success(`Request received · Ref ${data.ref}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split("T")[0];

  if (submitted) {
    const waMsg = encodeURIComponent(
      `Hi Tech World! I just submitted a repair request.\n\nRef: ${submitted.ref}\nDevice: ${submitted.brand} ${submitted.model}\nIssue: ${submitted.issue}\n\nPlease share the repair estimate.`
    );
    const waLink = `https://wa.me/${config.business_whatsapp || "918104878405"}?text=${waMsg}`;
    return (
      <div className="min-h-screen bg-background" data-testid="repair-success-page">
        <HeaderNav />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center mb-4 tv-pulse">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tighter">Repair request received!</h1>
          <p className="text-muted-foreground mt-2">
            Your reference is <span className="font-mono font-bold text-foreground" data-testid="repair-ref">{submitted.ref}</span>.
            Our Tech World technician will WhatsApp you shortly with the estimate.
          </p>
          <Card className="mt-8 text-left">
            <CardContent className="p-6 space-y-2 text-sm">
              <div><span className="text-muted-foreground">Device:</span> <span className="font-medium">{submitted.brand} {submitted.model}</span></div>
              <div><span className="text-muted-foreground">Issue:</span> <span className="font-medium">{submitted.issue}</span></div>
              <div><span className="text-muted-foreground">Service:</span> <span className="font-medium capitalize">{submitted.service_mode.replace("-", " ")}</span></div>
              <div><span className="text-muted-foreground">Contact:</span> <span className="font-medium">{submitted.customer_name} · {submitted.customer_phone}</span></div>
            </CardContent>
          </Card>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <a href={waLink} target="_blank" rel="noreferrer" data-testid="repair-whatsapp-link">
              <Button className="bg-[#25D366] hover:bg-[#20BD5A] text-white h-11 gap-2">
                Ping Tech World on WhatsApp
              </Button>
            </a>
            <Button variant="outline" onClick={() => navigate("/")} data-testid="back-home-btn">Back to Home</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="repair-page">
      <HeaderNav />
      <div className="relative overflow-hidden tv-hero-dark py-14 lg:py-20">
        <div className="absolute inset-0 tv-hero-dark-grid opacity-70 pointer-events-none" />
        <img src="/tech-value-logo.png" alt="" aria-hidden className="absolute -right-16 -top-8 w-96 h-auto opacity-[0.08] pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6">
          <span className="tv-chip-white mb-4">
            <Wrench className="w-3.5 h-3.5" /> Mobile & Laptop Repair
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter">
            Book a <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">repair</span> in 60 seconds.
          </h1>
          <p className="mt-3 text-slate-300 text-lg max-w-2xl">
            Tell us what&apos;s wrong — our Tech World technicians will WhatsApp you a free estimate. Walk-in or doorstep pickup, your choice.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <Card className="mb-6">
          <CardHeader><CardTitle>Repair Request Form</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-6" data-testid="repair-form">
              <div>
                <Label className="mb-3 block">Device Type *</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {DEVICE_TYPES.map((d) => {
                    const active = form.device_type === d.value;
                    return (
                      <button
                        type="button"
                        key={d.value}
                        onClick={() => update("device_type", d.value)}
                        data-testid={`device-${d.value}`}
                        className={`rounded-xl border-2 p-3 flex flex-col items-center gap-2 transition-all ${
                          active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                        }`}
                      >
                        <d.Icon className={`w-6 h-6 ${active ? "text-primary" : "text-muted-foreground"}`} />
                        <span className="text-sm font-medium">{d.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="brand">Brand *</Label>
                  <Input id="brand" value={form.brand} onChange={(e) => update("brand", e.target.value)} placeholder="e.g. Apple, Samsung, HP, Dell" data-testid="repair-brand" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="model">Model *</Label>
                  <Input id="model" value={form.model} onChange={(e) => update("model", e.target.value)} placeholder="e.g. iPhone 13 Pro / MacBook Air M1" data-testid="repair-model" />
                </div>
              </div>

              <div className="grid gap-2">
                <Label>What&apos;s the issue? *</Label>
                <Select value={form.issue} onValueChange={(v) => update("issue", v)}>
                  <SelectTrigger data-testid="repair-issue"><SelectValue placeholder="Pick the closest issue…" /></SelectTrigger>
                  <SelectContent>
                    {COMMON_ISSUES.map((i) => <SelectItem key={i} value={i} data-testid={`issue-${i.replace(/\s+/g,'-').toLowerCase()}`}>{i}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Textarea
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="Any additional details (optional)…"
                  data-testid="repair-notes"
                  rows={2}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="cn">Your Name *</Label>
                  <Input id="cn" value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} data-testid="repair-name" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="cp">WhatsApp Number *</Label>
                  <Input id="cp" value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} placeholder="10-digit number" data-testid="repair-phone" />
                </div>
              </div>

              <div>
                <Label className="mb-3 block">How would you like to service? *</Label>
                <div className="grid sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => update("service_mode", "walk-in")}
                    data-testid="mode-walkin"
                    className={`rounded-xl border-2 p-4 text-left transition-all ${form.service_mode === "walk-in" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                  >
                    <div className="font-semibold">Walk-in at Tech World</div>
                    <div className="text-xs text-muted-foreground mt-1">Vashi, Navi Mumbai · Free diagnostic</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => update("service_mode", "doorstep-pickup")}
                    data-testid="mode-pickup"
                    className={`rounded-xl border-2 p-4 text-left transition-all ${form.service_mode === "doorstep-pickup" ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                  >
                    <div className="font-semibold">Doorstep Pickup</div>
                    <div className="text-xs text-muted-foreground mt-1">Mumbai / Navi Mumbai · ₹200 handling fee</div>
                  </button>
                </div>
              </div>

              {form.service_mode === "doorstep-pickup" && (
                <div className="grid gap-2">
                  <Label htmlFor="addr">Address *</Label>
                  <Textarea id="addr" value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Flat, building, street, city, pincode" data-testid="repair-address" rows={2} />
                </div>
              )}

              <div className="grid gap-2">
                <Label htmlFor="pd">Preferred Date (optional)</Label>
                <Input id="pd" type="date" min={minDateStr} value={form.preferred_date} onChange={(e) => update("preferred_date", e.target.value)} data-testid="repair-preferred-date" />
              </div>

              <div className="rounded-xl border border-accent/30 bg-accent/5 p-3 text-xs flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 shrink-0 text-accent mt-0.5" />
                <span className="text-muted-foreground">
                  <span className="text-foreground font-semibold">Free diagnostic + estimate.</span> You pay only after approving the repair cost. 30-day warranty on all repair work.
                </span>
              </div>

              <Button type="submit" disabled={submitting} className="w-full h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:opacity-90 text-white font-bold" data-testid="submit-repair-btn">
                {submitting ? "Submitting…" : "Get Free Repair Estimate"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
